import { t } from "@travily/i18n";
import type FriendRowType from "@travily/types/models/ProfileFriendshipsRow";
import { Dialog, DialogTitle } from "@ui/components/Dialog";
import { EmptyState } from "@ui/components/EmptyState";
import { Icon } from "@ui/components/Icon";
import { Illustration } from "@ui/components/Illustration";
import { SectionTab, SectionTabList, SectionTabPanel, SectionTabs } from "@ui/components/SectionTabs";
import { StandardButton } from "@ui/components/StandardButton";
import { StandardTextField } from "@ui/components/StandardTextField";
import { tv } from "@ui/lib/tv";
import type { Key } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { FriendRow, type FriendRowActions } from "./components/FriendRow";

export type FriendshipTab = "active" | "pending";

const styles = tv({
  slots: {
    // Tabs double as the modal header, mirroring the language/currency modal.
    header: [
      "flex shrink-0 items-center justify-between border-neutral-100 border-b",
      "px-5 md:px-0",
      "max-md:pt-2 md:pt-5",
    ],
    // px-1 keeps the search/row focus rings clear of the body's scroll clip.
    content: "flex flex-col gap-3 px-1 pt-4",
  },
})();

export interface FriendshipsModalProps extends FriendRowActions {
  isOwnProfile: boolean;
  tab: FriendshipTab;
  onTabChange: (tab: FriendshipTab) => void;
  query: string;
  onQueryChange: (query: string) => void;
  friends: FriendRowType[];
  /** The query the current `friends` reflect (drives empty/no-results UI, not the live input). */
  loadedQuery: string;
  /** A replace load (tab switch or new query) is in flight. */
  isLoading: boolean;
  /** A subsequent page is loading. */
  isLoadingMore: boolean;
  /** The current load failed. */
  isError: boolean;
  onRetry: () => void;
  /** Account id of the row whose mutation is in flight, if any. */
  processingId: number | null;
  /**
   * Pagination trigger supplied by the page (an @inertiaui `WhenVisible` sentinel),
   * rendered after the rows while more pages remain. Inertia/modal deps stay in the
   * app layer; this component stays presentational.
   */
  paginationSlot?: ReactNode;
  onClose: () => void;
}

export function FriendshipsModal({
  isOwnProfile,
  tab,
  onTabChange,
  query,
  onQueryChange,
  friends,
  loadedQuery,
  isLoading,
  isLoadingMore,
  isError,
  onRetry,
  processingId,
  paginationSlot,
  onClose,
  ...actions
}: FriendshipsModalProps) {
  // Once a default (unqueried) load comes back empty the account has nothing to
  // search — hide the field rather than offer one that can't match. Keyed off the
  // loaded query so it doesn't flicker while a new query is in flight.
  const showSearch = !(loadedQuery.trim() === "" && friends.length === 0 && !isLoading && !isError);

  const list = (
    <FriendsList
      actions={actions}
      friends={friends}
      isError={isError}
      isLoadingMore={isLoadingMore}
      isOwnProfile={isOwnProfile}
      loadedQuery={loadedQuery}
      onRetry={onRetry}
      paginationSlot={paginationSlot}
      processingId={processingId}
      tab={tab}
    />
  );

  return (
    <>
      <DialogTitle className="sr-only">{t("common.profile.friends")}</DialogTitle>
      <SectionTabs
        className="flex flex-1 flex-col overflow-hidden"
        onSelectionChange={(key: Key) => onTabChange(key as FriendshipTab)}
        selectedKey={tab}
        size="header"
      >
        <div className={styles.header()}>
          <SectionTabList aria-label={t("common.profile.friends")}>
            <SectionTab id="active">{t("common.profile.friends")}</SectionTab>
            {isOwnProfile && <SectionTab id="pending">{t("common.friendships.pending")}</SectionTab>}
          </SectionTabList>
          <Dialog.CloseButton onPress={onClose} />
        </div>
        <Dialog.Body className="pt-0">
          <div className={styles.content()}>
            {showSearch && <SearchField onChange={onQueryChange} value={query} />}
            <SectionTabPanel id="active">{list}</SectionTabPanel>
            {isOwnProfile && <SectionTabPanel id="pending">{list}</SectionTabPanel>}
          </div>
        </Dialog.Body>
      </SectionTabs>
    </>
  );
}

function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <StandardTextField
      isLabelHidden
      label={t("common.friendships.search_placeholder")}
      leadIcon={<Icon name="search" type="sticker" />}
      onChange={onChange}
      placeholder={t("common.friendships.search_placeholder")}
      value={value}
    />
  );
}

interface FriendsListProps {
  actions: FriendRowActions;
  friends: FriendRowType[];
  isError: boolean;
  isLoadingMore: boolean;
  isOwnProfile: boolean;
  loadedQuery: string;
  onRetry: () => void;
  paginationSlot?: ReactNode;
  processingId: number | null;
  tab: FriendshipTab;
}

function FriendsList({
  actions,
  friends,
  isError,
  isLoadingMore,
  isOwnProfile,
  loadedQuery,
  onRetry,
  paginationSlot,
  processingId,
  tab,
}: FriendsListProps) {
  function renderFriendsContent() {
    // The current view is held until a reload's data actually lands (rows / no-results
    // for the loaded query / empty state are all keyed off settled state), so a replace
    // load never flashes a placeholder — it swaps straight to the new content.
    if (friends.length === 0) {
      if (isError) {
        return (
          <EmptyState
            action={
              <StandardButton color="neutral" hierarchy="primary" onPress={onRetry} size="44">
                {t("common.actions.retry")}
              </StandardButton>
            }
            className="w-full"
            description={t("common.friendships.action_error.description")}
            illustration={<Illustration name="error" />}
            title={t("common.friendships.action_error.title")}
          />
        );
      }

      if (loadedQuery.trim().length > 0) {
        return (
          <p className="py-10 text-center text-base text-neutral-400">
            {t("common.friendships.no_results", { query: loadedQuery })}
          </p>
        );
      }

      return <FriendsEmptyState isOwnProfile={isOwnProfile} tab={tab} />;
    }

    return (
      <div className="flex flex-col">
        {friends.map((friend) => (
          <FriendRow
            friend={friend}
            isProcessing={processingId === friend.id}
            key={friend.id}
            onAccept={actions.onAccept}
            onBecomeFriends={actions.onBecomeFriends}
            onDecline={actions.onDecline}
            onUnfriend={actions.onUnfriend}
          />
        ))}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <span className="size-6 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-500" />
          </div>
        )}
        {paginationSlot}
      </div>
    );
  }

  // min-h keeps the modal a steady height across empty / few-result states, so
  // searching never flashes the modal taller then shorter again.
  return <div className="min-h-[18rem]">{renderFriendsContent()}</div>;
}

function FriendsEmptyState({ isOwnProfile, tab }: { isOwnProfile: boolean; tab: FriendshipTab }) {
  if (tab === "pending") {
    return (
      <EmptyState
        className="w-full"
        description={t("common.friendships.empty_pending_body")}
        illustration={<Illustration name="restricted" />}
        title={t("common.friendships.empty_pending_title")}
      />
    );
  }
  return (
    <EmptyState
      className="w-full"
      // "you" copy on your own profile, "they" copy on someone else's.
      // i18n-tasks-use t('common.friendships.empty_body')
      // i18n-tasks-use t('common.friendships.empty_other_body')
      description={t(isOwnProfile ? "common.friendships.empty_body" : "common.friendships.empty_other_body")}
      illustration={<Illustration name="restricted" />}
      title={t("common.friendships.empty_title")}
    />
  );
}
